import { Link } from 'react-router-dom';
import Footer from '@/pages/home/components/Footer';

interface ArticlePageLayoutProps {
  title: string;
  description: string;
  children: React.ReactNode;
}

const articleLinks = [
  { name: 'Complete Spreadsheet Guide', path: '/sugargoo-spreadsheet-guide' },
  { name: 'Shipping Cost Guide', path: '/sugargoo-shipping-cost-guide' },
  { name: 'Is Sugargoo Legit?', path: '/is-sugargoo-legit' },
  { name: 'Coupon & Discount Guide', path: '/sugargoo-coupon-guide' },
  { name: 'Beginner Guide', path: '/sugargoo-beginner-guide' },
  { name: 'Best Finds 2026', path: '/sugargoo-best-finds' },
  { name: 'Warehouse Guide', path: '/sugargoo-warehouse-guide' },
  { name: 'Sugargoo vs Alternatives', path: '/sugargoo-vs-alternatives' },
  { name: 'Shopping Tips', path: '/sugargoo-shopping-tips' },
  { name: 'Spreadsheet Categories', path: '/sugargoo-spreadsheet-categories' },
];

export default function ArticlePageLayout({ title, description, children }: ArticlePageLayoutProps) {
  return (
    <div className="min-h-screen bg-background-50">
      <header className="py-8 md:py-12 px-4 md:px-6 bg-background-100 border-b border-background-200">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
            <Link to="/" className="hover:text-foreground-800 transition-colors duration-200 cursor-pointer">
              Home
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700 truncate">{title}</span>
          </div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-3 leading-tight">
            {title}
          </h1>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
            {description}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-4 text-xs text-foreground-400">
            <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> Updated August 2026</span>
            <span className="flex items-center gap-1"><i className="ri-time-line"></i> 10 min read</span>
            <span className="flex items-center gap-1"><i className="ri-user-line"></i> By Sugargoo Spreadsheet Hub</span>
          </div>
        </div>
      </header>

      <article className="py-10 md:py-14 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed">
            {children}
          </div>

          {/* CTA Section */}
          <div className="my-10 p-6 md:p-8 bg-accent-50 rounded-2xl border border-accent-200 text-center not-prose">
            <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-3">
              Ready to Start Shopping?
            </h3>
            <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
              Explore our organized Sugargoo Spreadsheet categories and discover verified products across shoes, clothing, accessories, and more.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Homepage
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-white font-semibold text-sm rounded-full hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shopping-bag-line"></i>
                Visit Main Website
              </a>
            </div>
          </div>

          {/* Related Articles */}
          <div className="my-8 p-5 bg-background-100 rounded-xl border border-background-200 not-prose">
            <h4 className="text-sm font-semibold text-foreground-800 mb-3">More Shopping Guides</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {articleLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="text-sm text-accent-700 hover:text-accent-800 transition-colors duration-200 flex items-center gap-1.5 py-1 cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-xs"></i>
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}